import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Bot, Copy, RefreshCw, Loader2, Eye, EyeOff } from 'lucide-react';

const API = window.location.origin;

export const ChatGPTConnectorCard = ({ authHeaders }) => {
  const [info, setInfo] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/connector/info`, authHeaders)
      .then((res) => setInfo(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regenerate = async () => {
    if (info?.configured && !window.confirm('Generate a new key? Your existing ChatGPT connection will stop working until you update the key there.')) return;
    setWorking(true);
    try {
      const res = await axios.post(`${API}/api/admin/connector/regenerate-key`, {}, authHeaders);
      setInfo((p) => ({ ...p, api_key: res.data.api_key, configured: true }));
      setShowKey(true);
      toast.success('Connector key generated — paste it into your Custom GPT');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate key');
    } finally {
      setWorking(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Card data-testid="chatgpt-connector-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-emerald-600" />
          ChatGPT Connector
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Let your personal ChatGPT (Plus) securely read your live business data — pipeline, quotes, outstanding invoices and upcoming jobs. Read-only; protected by the key below.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">1. Your connector API key</p>
          {info?.configured ? (
            <div className="flex gap-2">
              <Input
                readOnly
                type={showKey ? 'text' : 'password'}
                value={info.api_key || ''}
                className="font-mono text-xs"
                data-testid="connector-api-key"
              />
              <Button variant="outline" size="icon" onClick={() => setShowKey((v) => !v)} data-testid="connector-key-toggle">
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => copy(info.api_key, 'API key')} data-testid="connector-key-copy">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No key yet — generate one to get started.</p>
          )}
          <Button
            variant="outline"
            size="sm"
            className="mt-2 gap-1.5"
            onClick={regenerate}
            disabled={working}
            data-testid="connector-regenerate-btn"
          >
            {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {info?.configured ? 'Regenerate key' : 'Generate key'}
          </Button>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">2. Schema URL (for the GPT editor)</p>
          <div className="flex gap-2">
            <Input readOnly value={info?.schema_url || ''} className="font-mono text-xs" data-testid="connector-schema-url" />
            <Button variant="outline" size="icon" onClick={() => copy(info?.schema_url || '', 'Schema URL')} data-testid="connector-schema-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1.5">
          <p className="font-semibold text-xs text-muted-foreground uppercase">3. Set it up in ChatGPT (once)</p>
          <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
            <li>In ChatGPT go to <b>Explore GPTs → Create</b> and name it e.g. "My Handyman Business".</li>
            <li>Open the <b>Configure</b> tab → scroll to <b>Actions</b> → <b>Create new action</b>.</li>
            <li>Click <b>Import from URL</b> and paste the Schema URL above.</li>
            <li>Under <b>Authentication</b> choose <b>API Key</b> → Auth type <b>Custom</b> → header name <code>X-API-Key</code> → paste your connector key.</li>
            <li>Save. Now ask it things like "who owes me money?" or "what's booked next week?"</li>
          </ol>
          <p className="text-xs text-muted-foreground pt-1">
            Note: this works against your <b>live site</b> once deployed. Keep the key private — anyone with it can read your business data. Regenerate it to revoke access.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatGPTConnectorCard;
